/**
 * Screening Summary display rows for the frozen regulatory feasibility screen
 * (R7). Pure presentation: the saved screen is never changed or re-run.
 *
 * Display gate (saved bytes unchanged):
 *   - a missing or unknown status shows 'Unresolved';
 *   - a 'no blocker identified' or 'confirmed restriction' row shows
 *     'Unresolved (source not admitted)' unless an effective source backs it
 *     AND this report admits that specific source (the admitted report
 *     reference that backs that document source for a document basis; an
 *     admitted reference at the same URL for a provider);
 *   - a proposed rule or a proceeding is never labelled effective;
 *   - 'no blocker identified' always carries the not-an-approval qualifier and
 *     never takes a positive (green/teal) tone.
 */
import { UNRESOLVED_REASON_LABEL, type ConnectionScenario, type RegulatoryIssue, type RegulatoryIssueStatus, type RegulatorySource, type SourceRuleStatus } from '../regulatoryFeasibility/types';
import { fmtDate } from './text';
import {
  NO_BLOCKER_DISPLAY_QUALIFIER, NO_BLOCKER_QUALIFIER, REGULATORY_AUTHORITY_LEVEL_LABEL, REGULATORY_ISSUE_SHORT_LABEL, REGULATORY_RULE_STATUS_LABEL,
  REGULATORY_SCENARIO_LABEL, REGULATORY_SCENARIO_SHORT_LABEL, REGULATORY_SOURCE_NOT_ADMITTED_LABEL, REGULATORY_STATUS_LABEL,
} from './vocabulary';

/** Which saved report sources the Summary admits, by evidence basis. */
export interface RegulatorySourceSupport {
  /**
   * Screen source ids (for example 'zoning-document') whose OWN backing report
   * reference is admitted. A document-basis row is admitted only through one
   * of its effective sources in this set; an unrelated admitted upload never
   * backs it, and any other document source reads as not admitted.
   */
  readonly documentSourceIds: ReadonlySet<string>;
  /** URLs of admitted report sources. */
  readonly providerUrls: ReadonlySet<string>;
}

export type SummaryRegulatoryTone = 'high' | 'medium' | 'neutral';

export interface SummaryRegulatoryRow {
  id: string;
  /** The screen's issue id (one per issue; grid interface has one row per scenario). */
  issueId: string;
  issue: string;
  scenario: string;
  scenarioIds: ConnectionScenario[];
  /** Displayed status after the gate; never upgraded from the saved status. */
  status: RegulatoryIssueStatus;
  statusLabel: string;
  /** 'No blocker identified' only: the screen's own qualifier, verbatim. */
  qualifier: string | null;
  tone: SummaryRegulatoryTone;
  authority: string;
  source: string;
  sourceDate: string | null;
  ruleStatus: SourceRuleStatus | null;
}

const STATUS_TONE: Readonly<Record<RegulatoryIssueStatus, SummaryRegulatoryTone>> = {
  'confirmed-restriction': 'high',
  'approval-required': 'medium',
  unresolved: 'medium',
  'no-blocker-identified': 'neutral',
};

const isStatus = (value: unknown): value is RegulatoryIssueStatus => typeof value === 'string' && Object.hasOwn(REGULATORY_STATUS_LABEL, value);

function admitted(row: Partial<RegulatoryIssue>, effective: readonly RegulatorySource[], support: RegulatorySourceSupport): boolean {
  if (effective.length === 0) return false;
  if (row.evidenceBasis === 'document') return effective.some(source => support.documentSourceIds.has(source.id));
  if (row.evidenceBasis === 'provider') return effective.some(source => source.url != null && support.providerUrls.has(source.url));
  return false;
}

function scenarioText(row: Partial<RegulatoryIssue>, allScenarios: readonly ConnectionScenario[]): string {
  const scenarios = row.affectedScenarios ?? [];
  if (scenarios.length === 0) return REGULATORY_SCENARIO_LABEL.unknown;
  if (row.issue !== 'grid-interface' && scenarios.length > 1 && allScenarios.every(scenario => scenarios.includes(scenario))) return 'All recorded scenarios';
  return scenarios.map(scenario => REGULATORY_SCENARIO_LABEL[scenario] ?? REGULATORY_SCENARIO_LABEL.unknown).join('; ');
}

function authorityText(row: Partial<RegulatoryIssue>): string {
  const authority = row.authority;
  if (!authority || authority.level === 'unknown') {
    return authority?.name ? `Authority not evaluated (geographic candidate: ${authority.name})` : REGULATORY_AUTHORITY_LEVEL_LABEL.unknown;
  }
  const level = REGULATORY_AUTHORITY_LEVEL_LABEL[authority.level] ?? REGULATORY_AUTHORITY_LEVEL_LABEL.unknown;
  return authority.name ? `${level}: ${authority.name}` : `${level} (not named)`;
}

/** One display row. Accepts a partial row so a malformed status degrades to 'Unresolved'. */
export function summaryRegulatoryRow(row: Partial<RegulatoryIssue>, allScenarios: readonly ConnectionScenario[], support: RegulatorySourceSupport): SummaryRegulatoryRow {
  const saved: RegulatoryIssueStatus = isStatus(row.status) ? row.status : 'unresolved';
  const sources = row.sources ?? [];
  const effective = sources.filter(source => source.ruleStatus === 'effective');
  const decisive = saved === 'no-blocker-identified' || saved === 'confirmed-restriction';
  const gated = decisive && !admitted(row, effective, support);
  const status: RegulatoryIssueStatus = gated ? 'unresolved' : saved;
  const reason = !gated && saved === 'unresolved' && row.unresolvedReason ? UNRESOLVED_REASON_LABEL[row.unresolvedReason] : null;
  const statusLabel = gated ? REGULATORY_SOURCE_NOT_ADMITTED_LABEL : reason ? `${REGULATORY_STATUS_LABEL.unresolved} (${reason})` : REGULATORY_STATUS_LABEL[status];
  const primary = effective[0] ?? sources[0];
  const ruleStatus = primary?.ruleStatus ?? null;
  const sourceDate = primary?.asOf ? fmtDate(primary.asOf) : null;
  return {
    id: row.id ?? row.issue ?? 'regulatory-issue',
    issueId: row.issue ?? 'regulatory-issue',
    issue: row.issue ? REGULATORY_ISSUE_SHORT_LABEL[row.issue] ?? row.issue : 'Regulatory issue',
    scenario: scenarioText(row, allScenarios),
    scenarioIds: [...(row.affectedScenarios ?? [])],
    status,
    statusLabel,
    qualifier: status === 'no-blocker-identified' ? NO_BLOCKER_QUALIFIER : null,
    tone: STATUS_TONE[status],
    authority: authorityText(row),
    source: primary
      ? `${primary.label} · ${sourceDate ?? 'undated'} · ${REGULATORY_RULE_STATUS_LABEL[primary.ruleStatus] ?? REGULATORY_RULE_STATUS_LABEL.unknown}`
      : 'No source recorded',
    sourceDate,
    ruleStatus,
  };
}

/** Display rows in the screen's own issue order. */
export function summaryRegulatoryRows(issues: readonly Partial<RegulatoryIssue>[], support: RegulatorySourceSupport): SummaryRegulatoryRow[] {
  const allScenarios = [...new Set(issues.flatMap(issue => issue.affectedScenarios ?? []))];
  return issues.map(issue => summaryRegulatoryRow(issue, allScenarios, support));
}

/** One printed status: the scenario prefix (grid interface only), the gated
 * label and, for 'No blocker identified', the not-an-approval qualifier. */
export interface SummaryRegulatoryStatusText {
  id: string;
  status: RegulatoryIssueStatus;
  tone: SummaryRegulatoryTone;
  text: string;
}

/** One printed issue line. Every saved row appears as exactly one status. */
export interface SummaryRegulatoryIssueLine {
  issueId: string;
  issue: string;
  tone: SummaryRegulatoryTone;
  statuses: SummaryRegulatoryStatusText[];
  /** Actionable confirmation/deliverable -> decision gate when unresolved or approval-required. */
  actionableGate?: string;
}

const TONE_RANK: Readonly<Record<SummaryRegulatoryTone, number>> = { neutral: 0, medium: 1, high: 2 };

export const REGULATORY_ACTIONABLE_GATE: Readonly<Record<string, string>> = {
  'land-use-data-center': 'zoning/use determination → data-center siting',
  'land-use-generation': 'zoning/use determination → on-site plant siting',
  'air-permit-operating-duty': 'air-agency applicability & duty ruling → combustion procurement',
  'ownership-electricity-sale': 'state-law & utility-territory review → ownership/sale structure',
  'grid-interface': 'utility/ISO interconnection & protection study → parallel/islanded mode',
  'grid-service-economics': 'will-serve / standby & service terms → import & outage economics',
  'water-wastewater': 'water-rights/will-serve & wastewater discharge confirmation → cooling & plant water commitment',
};

/**
 * Compact printed form for the three-page Summary: one line per issue, with
 * the grid-interface scenarios listed inside their issue. Nothing is dropped:
 * each saved row contributes exactly one status text.
 */
export function summaryRegulatoryIssueLines(rows: readonly SummaryRegulatoryRow[]): SummaryRegulatoryIssueLine[] {
  const lines: SummaryRegulatoryIssueLine[] = [];
  const gridRows = rows.filter(r => r.issueId === 'grid-interface');
  const allGridSameUnresolved = gridRows.length > 1
    && gridRows.every(r => r.status === 'unresolved' && r.statusLabel === 'Unresolved (not yet screened)');
  for (const row of rows) {
    // Grid-interface rows are per scenario; every other issue is one row.
    const scenario = row.issueId === 'grid-interface'
      ? row.scenarioIds.map(id => REGULATORY_SCENARIO_SHORT_LABEL[id] ?? REGULATORY_SCENARIO_SHORT_LABEL.unknown).join('; ') || REGULATORY_SCENARIO_SHORT_LABEL.unknown
      : null;
    const displayStatusLabel = row.statusLabel === REGULATORY_SOURCE_NOT_ADMITTED_LABEL
      ? 'Confirmation required (parcel source not verified)'
      : row.statusLabel === 'Unresolved (not yet screened)'
        ? 'Confirmation required'
        : row.statusLabel === 'Unresolved (screened, still open)'
          ? 'Confirmation required (screened, still open)'
          : row.statusLabel;
    const isLastGridRow = row.issueId === 'grid-interface' && gridRows[gridRows.length - 1]?.id === row.id;
    const text = row.issueId === 'grid-interface' && allGridSameUnresolved
      ? (isLastGridRow ? `${scenario}: confirmation required across all connection modes` : `${scenario}`)
      : `${scenario ? `${scenario}: ` : ''}${displayStatusLabel}${row.qualifier ? ` (${NO_BLOCKER_DISPLAY_QUALIFIER})` : ''}`;
    const status: SummaryRegulatoryStatusText = {
      id: row.id, status: row.status, tone: row.tone,
      text,
    };
    const line = lines.find(candidate => candidate.issueId === row.issueId);
    if (line) {
      line.statuses.push(status);
      if (TONE_RANK[status.tone] > TONE_RANK[line.tone]) line.tone = status.tone;
    } else {
      lines.push({ issueId: row.issueId, issue: row.issue, tone: row.tone, statuses: [status] });
    }
  }
  for (const line of lines) {
    if (line.statuses.some(s => s.status === 'unresolved' || s.status === 'approval-required')) {
      const gate = REGULATORY_ACTIONABLE_GATE[line.issueId];
      if (gate) line.actionableGate = gate;
    }
  }
  return lines;
}
